import { Button, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { DamageCostEvaluationDto } from "@/core/api/generated/v1";

import DamageCostEvaluationAutocomplete, {
  DamageCostEvaluationAutocompleteProps,
} from "./DamageCostEvaluationAutocomplete";
import DamageCostEvaluationCreateUpdate, {
  DamageCostEvaluationCreateUpdateProps,
} from "./DamageCostEvaluationCreateUpdate";
import DamageCostEvaluationCreateUpdateModal from "./DamageCostEvaluationCreateUpdateModal";

interface OwnProps {
  autocompleteProps: DamageCostEvaluationAutocompleteProps;
  createUpdateProps?: Pick<DamageCostEvaluationCreateUpdateProps, "defaultValues" | "displayProps">;
  createFormPlacement?: "modal" | "page";
  onCreateStart?: () => void;
  onCreate: (newValue: DamageCostEvaluationDto) => void;
}

type Props = OwnProps;

export default function DamageCostEvaluationAutocompleteOrCreate({
  autocompleteProps,
  createUpdateProps,
  createFormPlacement = "modal",
  onCreateStart,
  onCreate,
}: Props) {
  const [isCreate, setIsCreate] = useState(false);
  const [defaultValues, setDefaultValues] = useState<
    DamageCostEvaluationCreateUpdateProps["defaultValues"] | undefined
  >(undefined);

  return (
    <Stack spacing={2}>
      {/* 1. Search */}
      {!isCreate && (
        <Box>
          <DamageCostEvaluationAutocomplete
            {...autocompleteProps}
            withCreate
            createOptionTitle={(inputValue) => `Create new damage cost evaluation "${inputValue}"`}
            onCreate={(newOption) => {
              setDefaultValues({
                ...createUpdateProps?.defaultValues,
              });
              setIsCreate(true);
              onCreateStart && onCreateStart();
            }}
          />
        </Box>
      )}

      {/* 2. Create */}
      {isCreate && (
        <Box>
          {isCreate && (
            <>
              {createFormPlacement === "modal" && (
                <DamageCostEvaluationCreateUpdateModal
                  open={isCreate}
                  onClose={() => setIsCreate(false)}
                  createUpdateProps={{
                    ...createUpdateProps,
                    defaultValues: defaultValues,
                    onCreate: onCreate,
                    onSave: (newValue) => {
                      setIsCreate(false);
                    },
                  }}
                />
              )}

              {createFormPlacement === "page" && (
                <Stack spacing={1}>
                  <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
                    <Button
                      variant='text'
                      color='text'
                      size='small'
                      startIcon={<AppIcon of='goBack' />}
                      onClick={() => setIsCreate(false)}
                    >
                      Back to search
                    </Button>

                    <Typography component='div' variant='subtitle1'>
                      Create new damage cost evaluation
                    </Typography>
                  </Stack>

                  <DamageCostEvaluationCreateUpdate
                    defaultValues={defaultValues}
                    onCreate={onCreate}
                    onSave={(newValue) => {
                      setIsCreate(false);
                    }}
                  />
                </Stack>
              )}
            </>
          )}
        </Box>
      )}
    </Stack>
  );
}
